<template>
  <g class="in-node">
    <transition-group v-if="expanded" name="in-sub-pop" tag="g">
      <path
        v-for="sub in subNodes"
        :key="'link-' + (sub.kind === 'rest' ? 'rest' : sub.variable)"
        :d="linkPath(sub)"
        class="in-link"
        :style="{ stroke: color }"
      />
    </transition-group>

    <g class="in-hub" v-tooltip.top="hubTooltipConfig" v-on:click="onToggle">
      <circle
        :cx="cx"
        :cy="cy"
        :r="radius"
        :class="{
          'in-circle': true,
          'in-circle--nomodel': isNoModel,
          'in-circle--dashed': !isNoModel && dominantBetaSign < 0,
        }"
        :style="isNoModel ? {} : { fill: wash, stroke: color }"
      />
      <template v-if="!isNoModel">
        <text :x="cx" :y="cy - 20" class="in-label" text-anchor="middle" :style="{ fill: color }">
          {{ label }}
        </text>
        <text :x="cx" :y="cy" class="in-value" text-anchor="middle" :style="{ fill: color }">
          {{ r2 }}%
        </text>
        <text
          :x="cx"
          :y="cy + 16"
          class="in-collapsed-text"
          text-anchor="middle"
          :style="{ fill: color }"
        >
          {{ conditionCount }} de 16 condiciones
        </text>
        <text
          :x="cx"
          :y="cy + 30"
          class="in-hint"
          text-anchor="middle"
          :style="{ fill: color }"
        >
          {{ expanded ? "Clic para colapsar" : "Clic para ver más" }}
        </text>
      </template>
      <template v-else>
        <text :x="cx" :y="cy - 8" class="in-label" text-anchor="middle">
          {{ label }}
        </text>
        <text :x="cx" :y="cy + 12" class="in-nomodel-text" text-anchor="middle">
          Sin modelo
        </text>
      </template>
      <text
        v-if="showWarningIcon"
        :x="cx + radius - 4"
        :y="cy - radius + 4"
        class="in-warning"
        text-anchor="middle"
        v-tooltip.top="warningsText"
      >
        !
      </text>
    </g>

    <transition-group v-if="expanded" name="in-sub-pop" tag="g">
      <g
        v-for="sub in subNodes"
        :key="sub.kind === 'rest' ? 'rest' : sub.variable"
        class="in-sub"
        v-tooltip.top="sub.kind === 'rest' ? restTooltipConfig(sub) : subTooltipConfig(sub)"
      >
        <circle
          :cx="sub.x"
          :cy="sub.y"
          :r="sub.radius"
          :class="{
            'in-sub-circle': true,
            'in-sub-circle--rest': sub.kind === 'rest',
            'in-sub-circle--dashed': sub.kind === 'condition' && sub.beta < 0,
          }"
          :style="{ fill: wash, stroke: color }"
        />
        <text
          v-for="(line, li) in sub.labelLines"
          :key="li"
          :x="sub.x"
          :y="sub.y + line.dy"
          class="in-sub-label"
          text-anchor="middle"
        >
          {{ line.text }}
        </text>
      </g>
    </transition-group>
  </g>
</template>

<script>
import {
  nonConstantCoefficients,
  betaProportions,
  roundRSquaredAdj,
  nodeSize,
  confidenceLabel,
  getConditionLabel,
  NOMODEL_RADIUS,
} from "./regression-helpers";

// El SVG no recorta texto solo: se mide con la misma fuente de .in-sub-label
// y se corta hasta que quepa en el diámetro de la burbuja.
const SUB_LABEL_FONT = '9px "Inter", sans-serif';
const SUB_LABEL_PADDING = 10;
let measureContext = null;

function measureSubLabel(text) {
  if (!measureContext) {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  measureContext.font = SUB_LABEL_FONT;
  return measureContext.measureText(text).width;
}

function subLabelLines(text, radius) {
  const maxWidth = radius * 2 - SUB_LABEL_PADDING;
  let line = text;
  if (measureSubLabel(line) > maxWidth) {
    let cut = text.length - 1;
    while (cut > 1 && measureSubLabel(text.slice(0, cut).replace(/\s+$/, "") + "…") > maxWidth) {
      cut -= 1;
    }
    line = text.slice(0, cut).replace(/\s+$/, "") + "…";
  }
  return [{ text: line, dy: 3 }];
}

// Pedido de Esteban 2026-09-30: con hasta 16 condiciones seleccionables el
// abanico se volvía ilegible — se muestran solo las 4 con mayor |beta| como
// sub-nodo propio, y el resto se agrupa en un único sub-nodo "N más" (mismo
// tamaño proporcional a la suma de sus proporciones), con el detalle de cada
// una en su tooltip.
const VISIBLE_SUB_MAX = 4;

const CONFIDENCE_COLORS = {
  "alta confianza": { bg: "#d9ead3", border: "#c3eab5", ink: "#3d6b3f" },
  "confianza moderada": { bg: "#fff2cc", border: "#f6db8a", ink: "#8a6d1f" },
  "confianza baja": { bg: "#facca5", border: "#f6b884", ink: "#95591f" },
};

export default {
  name: "IntralaboralNode",
  props: {
    analysis: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    effectLabel: {
      type: String,
      default: "",
    },
    cx: {
      type: Number,
      required: true,
    },
    cy: {
      type: Number,
      required: true,
    },
    hubCx: {
      type: Number,
      required: true,
    },
    hubCy: {
      type: Number,
      required: true,
    },
    angleDeg: {
      type: Number,
      default: 0,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "#6a6a6a",
    },
    wash: {
      type: String,
      default: "#f3eaf9",
    },
  },
  computed: {
    isNoModel() {
      return !this.analysis || this.analysis.no_model === true;
    },
    nonConstantRows() {
      return nonConstantCoefficients(this.analysis);
    },
    conditionCount() {
      return this.nonConstantRows.length;
    },
    r2() {
      return roundRSquaredAdj(this.analysis);
    },
    radius() {
      return this.isNoModel ? NOMODEL_RADIUS : nodeSize(this.r2);
    },
    n() {
      return this.analysis && this.analysis.n != null ? this.analysis.n : null;
    },
    dominantBetaSign() {
      if (!this.nonConstantRows.length) return 0;
      const sorted = this.nonConstantRows.slice().sort(function(a, b) {
        return Math.abs(b.beta || 0) - Math.abs(a.beta || 0);
      });
      return sorted[0].beta || 0;
    },
    sortedRows() {
      return this.nonConstantRows.slice().sort(function(a, b) {
        return Math.abs(b.beta || 0) - Math.abs(a.beta || 0);
      });
    },
    warnings() {
      return (this.analysis && this.analysis.warnings) || [];
    },
    showWarningIcon() {
      return !this.isNoModel && this.warnings.length > 0;
    },
    warningsText() {
      return this.warnings.join(" ");
    },
    hubTooltipConfig() {
      if (this.isNoModel) {
        const content =
          "<b>" +
          this.label +
          '</b><div class="rn-tt-sub">Sin modelo — muestra insuficiente</div>' +
          (this.n != null
            ? '<div class="rn-tt-row"><span>n efectivo</span><span>' + this.n + "</span></div>"
            : "") +
          (this.warnings.length ? '<div class="rn-tt-sub">' + this.warningsText + "</div>" : "");
        return { content: content, html: true, classes: ["rn-tooltip"] };
      }
      const rows = this.sortedRows
        .map((c) => {
          const beta = c.beta >= 0 ? "+" + c.beta.toFixed(2) : "−" + Math.abs(c.beta).toFixed(2);
          return (
            '<div class="rn-tt-row"><span>' +
            getConditionLabel(c.variable) +
            "</span><span>" +
            beta +
            "</span></div>"
          );
        })
        .join("");
      const sub =
        this.conditionCount +
        " de 16 condiciones seleccionadas · R² " +
        this.r2 +
        "%" +
        (this.n != null ? " · n=" + this.n : "");
      return {
        content: "<b>" + this.label + "</b>" + '<div class="rn-tt-sub">' + sub + "</div>" + rows,
        html: true,
        classes: ["rn-tooltip"],
      };
    },
    // Las 4 condiciones con mayor |beta| se muestran cada una en su propio
    // sub-nodo; el resto (si sobra alguna) se agrupa en un solo item "rest".
    displaySubItems() {
      const proportions = betaProportions(this.analysis);
      if (!proportions.length) return [];
      const sorted = proportions.slice().sort(function(a, b) {
        return Math.abs(b.beta || 0) - Math.abs(a.beta || 0);
      });
      const visible = sorted.slice(0, VISIBLE_SUB_MAX);
      const rest = sorted.slice(VISIBLE_SUB_MAX);
      const items = visible.map((p) => {
        const row = this.nonConstantRows.filter(function(c) {
          return c.variable === p.variable;
        })[0];
        return {
          kind: "condition",
          variable: p.variable,
          label: getConditionLabel(p.variable),
          beta: p.beta,
          pct: p.proportion * (this.r2 || 0),
          confidence: row ? confidenceLabel(row.p_value) : null,
        };
      });
      if (rest.length) {
        const restPct = rest.reduce(function(sum, p) {
          return sum + p.proportion;
        }, 0) * (this.r2 || 0);
        items.push({
          kind: "rest",
          label: rest.length + " más",
          pct: restPct,
          restItems: rest.map((p) => {
            return { variable: p.variable, label: getConditionLabel(p.variable), beta: p.beta };
          }),
        });
      }
      return items;
    },
    fanAngleDeg() {
      const n = this.displaySubItems.length;
      return Math.min(90, 42 * n);
    },
    subNodes() {
      const items = this.displaySubItems;
      const n = items.length;
      if (!n) return [];
      const radii = items.map(function(it) {
        return nodeSize(Math.round(it.pct));
      });
      const maxSubRadius = Math.max.apply(null, radii);
      // design.md §3.4/§6.1 — igual que el Artifact: el abanico de hijos se
      // mide desde el hub, sumando cuánto ya está el propio dominio alejado
      // de él más su radio y el radio del hijo más grande, con el mismo
      // margen de 30px, en vez de un offset fijo sin relación con los
      // tamaños reales.
      const fanRadius = this.radiusFromHub() + this.radius + maxSubRadius + 30;
      const spread = this.fanAngleDeg;
      const step = n > 1 ? spread / (n - 1) : 0;
      const startAngle = this.angleDeg - spread / 2;

      return items.map((it, i) => {
        const angle = n > 1 ? startAngle + step * i : this.angleDeg;
        const rad = (angle * Math.PI) / 180;
        const x = this.hubCx + fanRadius * Math.sin(rad);
        const y = this.hubCy - fanRadius * Math.cos(rad);
        return {
          kind: it.kind,
          variable: it.variable,
          label: it.label,
          labelLines: subLabelLines(it.label, radii[i]),
          beta: it.kind === "condition" ? it.beta : null,
          radius: radii[i],
          x: x,
          y: y,
          confidence: it.kind === "condition" ? it.confidence : null,
          restItems: it.restItems,
        };
      });
    },
  },
  methods: {
    radiusFromHub() {
      const dx = this.cx - this.hubCx;
      const dy = this.cy - this.hubCy;
      return Math.sqrt(dx * dx + dy * dy);
    },
    linkPath(sub) {
      return "M" + this.cx + "," + this.cy + " L" + sub.x + "," + sub.y;
    },
    subTooltipConfig(sub) {
      const conf = CONFIDENCE_COLORS[sub.confidence];
      const beta = sub.beta >= 0 ? "+" + sub.beta.toFixed(2) : "−" + Math.abs(sub.beta).toFixed(2);
      const content =
        "<b>" +
        sub.label +
        "</b>" +
        '<div class="rn-tt-sub">Intralaboral' +
        (this.effectLabel ? " · relación con " + this.effectLabel : "") +
        "</div>" +
        '<div class="rn-tt-row"><span>β estandarizado</span><span>' +
        beta +
        "</span></div>" +
        (conf
          ? '<div class="rn-tt-row"><span>Confiabilidad</span><span class="rn-tt-chip" style="background:' +
            conf.bg +
            ";border-color:" +
            conf.border +
            ";color:" +
            conf.ink +
            '">' +
            sub.confidence +
            "</span></div>"
          : "");
      return { content: content, html: true, classes: ["rn-tooltip"] };
    },
    restTooltipConfig(sub) {
      const rows = sub.restItems
        .map(function(item) {
          const beta = item.beta >= 0 ? "+" + item.beta.toFixed(2) : "−" + Math.abs(item.beta).toFixed(2);
          return (
            '<div class="rn-tt-row"><span>' + item.label + "</span><span>" + beta + "</span></div>"
          );
        })
        .join("");
      const content =
        "<b>" +
        sub.label +
        "</b>" +
        '<div class="rn-tt-sub">Intralaboral' +
        (this.effectLabel ? " · relación con " + this.effectLabel : "") +
        "</div>" +
        rows;
      return { content: content, html: true, classes: ["rn-tooltip"] };
    },
    onToggle() {
      this.$emit("toggle");
    },
  },
};
</script>

<style lang="scss" scoped>
.in-hub {
  cursor: pointer;
  outline: none;
}

.in-hub:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

.in-circle {
  fill: #f3eaf9;
  stroke: #6a6a6a;
  stroke-width: 1.5;
  stroke-dasharray: none;
}

.in-circle--dashed {
  stroke-dasharray: 4 3;
}

@media (prefers-reduced-motion: no-preference) {
  .in-circle,
  .in-sub-circle {
    transition: r 0.4s cubic-bezier(0.34, 1.3, 0.64, 1),
      cx 0.4s cubic-bezier(0.34, 1.3, 0.64, 1), cy 0.4s cubic-bezier(0.34, 1.3, 0.64, 1);
  }
}

.in-circle--nomodel {
  fill: #f4cdcd;
  fill-opacity: 0.45;
  stroke: #f6adad;
  stroke-dasharray: 4 3;
}

.in-label {
  font-family: "Poppins", sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  fill: #363636;
}

.in-value {
  font-family: "Poppins", sans-serif;
  font-size: 17px;
  font-weight: 700;
  fill: #363636;
}

.in-collapsed-text {
  font-family: "Inter", sans-serif;
  font-size: 9.5px;
  fill: #363636;
}

.in-hint {
  font-family: "Inter", sans-serif;
  font-size: 8.5px;
  font-weight: 600;
  fill: #363636;
}

.in-nomodel-text {
  font-family: "Inter", sans-serif;
  font-size: 10px;
  fill: #a12a2a;
}

.in-warning {
  font-size: 12px;
  fill: #e0a640;
  cursor: help;
  outline: none;
}

.in-warning:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

.in-link {
  fill: none;
  stroke-width: 1.4;
}

@media (prefers-reduced-motion: no-preference) {
  .in-link {
    transition: opacity 0.3s ease;
  }
}

.in-sub {
  cursor: default;
  outline: none;
}

.in-sub:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: no-preference) {
  .in-sub-pop-enter-active,
  .in-sub-pop-leave-active {
    transition: opacity 0.3s ease;
  }

  .in-sub-pop-enter,
  .in-sub-pop-leave-to {
    opacity: 0;
  }
}

.in-sub-circle {
  fill: #f3eaf9;
  stroke: #6a6a6a;
  stroke-width: 1.5;
  stroke-dasharray: none;
}

.in-sub-circle--dashed {
  stroke-dasharray: 3 2;
}

.in-sub-circle--rest {
  stroke-dasharray: 1.5 2;
}

.in-sub-label {
  font-family: "Inter", sans-serif;
  font-size: 9px;
  fill: #363636;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/variables-prioritarias/IntralaboralNode.vue